import React, { useCallback, useMemo, useState } from 'react';

import { getCrmRecordCopy } from '@aline/common';
import { Input, InputAdornment, List, Skeleton, Typography } from '@mui/material';
import type { ExistingFieldRecipient } from 'src/components';
import {
  AlineTooltip,
  FieldRecipientAutoComplete,
  SmallAlineButton,
  workflowStepsDialogOpenVar,
} from 'src/components';
import { LinkIcon } from 'src/components/icons/LinkIcon';
import { NotStaleIcon } from 'src/components/icons/NotStaleIcon';
import { SearchIcon } from 'src/components/icons/SearchIcon';
import { StaleIcon } from 'src/components/icons/StaleIcon';
import { TemplateLinkedIcon } from 'src/components/icons/TemplateLinkedIcon';
import { UnLinkIcon } from 'src/components/icons/UnLinkIcon';
import { useMaybeEditorRuntime } from 'src/editor/editor-runtime-registry';
import {
  getEditorTabStateActionsForTabId,
  useEditorTabStateSelectorForTabId,
} from 'src/editor/editor-tab-state-registry';
import {
  IntegrationFieldButton,
  SyncIntegrationButton,
} from 'src/editor/toolbars/right-panel/integrations/IntegrationFieldButton';
import { LinkWorkflowToOpportunityDialog } from 'src/editor/toolbars/right-panel/integrations/LinkOpportunityDialog';
import { Apollo } from 'src/gql/generated';
import { INTEGRATION_DISPLAY_NAME } from 'src/helpers/integration-display-name';
import { useWorkflowLinkedOpportunity } from 'src/hooks';
import { createClampStyles } from 'src/utils/styles';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import type { DocumentCRMField, FieldGroup } from './hooks';
import {
  getFieldGroupKey,
  getFieldGroups,
  useGetIntegrationFieldsFromLinkedOpp,
  useUpdateIntegrationFieldsInDoc,
} from './hooks';

const useStyles = makeStyles()(theme => ({
  actionButtons: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    paddingTop: theme.spacing(2),
  },
  assigneeInput: {
    '& .MuiAutocomplete-clearIndicator svg': {
      height: 16,
      width: 16,
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
      height: 28,
      margin: 0,
      padding: theme.spacing(0.5),
      width: 28,
    },
    '& .MuiAutocomplete-endAdornment': {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(0.25),
      right: theme.spacing(0.5),
      top: '50%',
      transform: 'translateY(-50%)',
    },
    '& .MuiAutocomplete-input': {
      padding: `${theme.spacing(0.75, 0.5)} !important`,
    },
    '& .MuiAvatar-root': {
      fontSize: '0.75rem',
      height: 24,
      width: 24,
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.common.white,
      minHeight: 36,
      padding: theme.spacing(0, 8, 0, 1),
    },
  },
  attributeCards: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  attributesHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  categoryHeader: {
    marginBottom: theme.spacing(1),
  },
  header: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  headerSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  linkCard: {
    background: 'rgba(20, 110, 232, 0.10)',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    boxShadow: `3px 4px 0px 0px ${theme.appColors.rule}`,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(2),
  },
  loadingCard: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    width: '100%',
  },
  loadingCardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.5, 1),
  },
  loadingSection: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  main: {
    backgroundColor: theme.appColors.background,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  noOppTitleContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  root: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    position: 'relative',
  },
  searchIcon: {
    color: theme.palette.text.secondary,
  },
  searchInput: {
    '&.Mui-focused': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
    },
    '&:hover:not(.Mui-focused)': {
      borderColor: theme.palette.text.secondary,
    },
    backgroundColor: 'transparent',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    boxSizing: 'border-box',
    minHeight: 36,
    padding: theme.spacing(0.25, 1),
    transition: theme.transitions.create(['border-color', 'box-shadow'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  templateHeader: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    justifyContent: 'center',
  },
  templateLink: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  titleText: {
    ...createClampStyles(3),
  },
}));

export function LinkOpportunity({
  documentId,
  documentVariant,
  integration,
  workflowId,
}: {
  documentId: UUID;
  documentVariant: Apollo.DocumentVariant;
  integration: Apollo.Integration;
  workflowId: UUID;
}) {
  const { classes } = useStyles();
  const { linkedOpportunity, loading: loadingLinkedOpp } = useWorkflowLinkedOpportunity({
    integration,
    workflowId,
  });
  const {
    apiDeckOpportunity,
    documentIntegrationFields,
    loading: loadingIntegrationFields,
    recordDeleted,
  } = useGetIntegrationFieldsFromLinkedOpp({ integration, linkedOpportunity, workflowId });
  const loading = loadingLinkedOpp || loadingIntegrationFields;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <OpportunityHeader
          apiDeckOpp={apiDeckOpportunity}
          documentId={documentId}
          documentVariant={documentVariant}
          integration={integration}
          integrationFields={documentIntegrationFields}
          linkedOpp={linkedOpportunity}
          loading={loading}
          recordDeleted={recordDeleted}
          workflowId={workflowId}
        />
      </div>
      <IntegrationFields
        className={classes.main}
        integration={integration}
        integrationFields={documentIntegrationFields}
        loading={loading}
      />
    </div>
  );
}

interface OpportunityTemplateHeaderProps {
  integration: Apollo.Integration;
}

export function OpportunityTemplateHeader({ integration }: OpportunityTemplateHeaderProps) {
  const { classes } = useStyles();

  return (
    <div className={classes.templateHeader}>
      <TemplateLinkedIcon />
      <div className={classes.templateLink}>
        <Typography align="center" variant="subtitle1">
          Add integration fields to your template
        </Typography>
        <Typography align="center">
          {`Connect your template to ${INTEGRATION_DISPLAY_NAME[integration]} by dragging fields into your document.`}
        </Typography>
      </div>
    </div>
  );
}

export function OpportunityHeader({
  apiDeckOpp,
  documentId,
  documentVariant,
  integration,
  integrationFields,
  linkedOpp,
  loading,
  recordDeleted,
  workflowId,
}: {
  apiDeckOpp: Pick<Apollo.ApiDeckOpportunityWithRelations, 'id' | 'title' | '__typename'> | null;
  documentId: UUID;
  documentVariant: Apollo.DocumentVariant;
  integration: Apollo.Integration;
  integrationFields: DocumentCRMField[];
  linkedOpp: Pick<Apollo.LinkedOpportunity, 'id' | '__typename'> | null;
  loading: boolean;
  /** The linked record was deleted in the CRM, so the link has nothing left to read. */
  recordDeleted: boolean;
  workflowId: UUID;
}) {
  const { classes } = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [linkWorkflow] = Apollo.useLinkWorkflowOpportunityMutation();
  const [unlinkWorkflowOpportunity, { loading: unlinkingOpportunity }] =
    Apollo.useUnlinkWorkflowOpportunityMutation();
  const crmName = INTEGRATION_DISPLAY_NAME[integration];
  const { article, label, lower } = getCrmRecordCopy(integration);
  const linkText = `Link document to ${article} ${crmName} ${lower}:`;
  // Only a live link can point at a deleted record; a cached title or a stale error must not win.
  const isRecordDeleted = Boolean(linkedOpp) && recordDeleted;
  const recordTitle = isRecordDeleted
    ? 'Deleted'
    : linkedOpp && apiDeckOpp
      ? apiDeckOpp.title || 'Untitled'
      : 'Unlinked';

  if (documentVariant === Apollo.DocumentVariant.Template)
    return <OpportunityTemplateHeader integration={integration} />;

  if (loading)
    return (
      <>
        <Typography variant="subtitle2">{linkText}</Typography>
        <div className={classes.linkCard}>
          <div className={classes.title}>
            <div>
              <Typography variant="subtitle2">{label}</Typography>
              <Typography>
                <Skeleton variant="text" width={56} />
              </Typography>
            </div>
            <Skeleton height={26} variant="rectangular" width={26} />
          </div>
          <div className={classes.actionButtons}>
            <Skeleton
              style={{
                borderRadius: 16,
                height: 24,
              }}
              variant="rounded"
              width={88}
            />
          </div>
        </div>
      </>
    );

  return (
    <>
      <Typography variant="subtitle2">{linkText}</Typography>
      <div className={classes.linkCard}>
        <div className={classes.title}>
          <div>
            <Typography variant="subtitle2">{label}</Typography>
            <Typography className={classes.titleText}>{recordTitle}</Typography>
          </div>
          {linkedOpp && !isRecordDeleted && (
            <StaleFieldsActions integration={integration} integrationFields={integrationFields} />
          )}
        </div>
        <div className={classes.actionButtons}>
          <SmallAlineButton
            color="primary"
            loading={unlinkingOpportunity}
            startIcon={linkedOpp ? <UnLinkIcon /> : <LinkIcon />}
            variant="outlined"
            onClick={async () => {
              if (!linkedOpp) {
                setOpen(true);
              } else {
                await unlinkWorkflowOpportunity({
                  variables: {
                    documentId,
                    linkedOpportunityId: linkedOpp.id,
                  },
                });
              }
            }}
          >
            {linkedOpp ? 'Unlink' : 'Link'}
          </SmallAlineButton>
        </div>
      </div>
      <LinkWorkflowToOpportunityDialog
        integration={integration}
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={async selectedOpportunityId => {
          await linkWorkflow({
            variables: {
              input: {
                integration,
                opportunityId: selectedOpportunityId,
                workflowId,
              },
            },
          });
        }}
      />
    </>
  );
}

const useStaleStyles = makeStyles()(theme => ({
  iconWrapper: {
    alignItems: 'center',
    display: 'flex',
    height: 26,
    justifyContent: 'center',
    width: 26,
  },
  staleFieldsActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
}));

export function StaleFieldsActions({
  integration,
  integrationFields,
}: {
  integration: Apollo.Integration;
  integrationFields: DocumentCRMField[];
}) {
  const { classes } = useStaleStyles();
  const crmName = INTEGRATION_DISPLAY_NAME[integration];
  const { label, lower } = getCrmRecordCopy(integration);
  const staleFields = integrationFields.filter(
    integrationField => integrationField.fieldInDocInfo?.isStale
  );
  const { updateIntegrationFieldsInDoc } = useUpdateIntegrationFieldsInDoc();

  if (!staleFields.length)
    return (
      <AlineTooltip
        arrow
        placement="top"
        title={`${label} field values match this document (${crmName})`}
      >
        <div className={classes.iconWrapper}>
          <NotStaleIcon />
        </div>
      </AlineTooltip>
    );

  return (
    <div className={classes.staleFieldsActions}>
      <SyncIntegrationButton
        buttonProps={{
          onClick: () => {
            updateIntegrationFieldsInDoc(staleFields);
          },
        }}
        title={`Pull ${lower} field values into document (${crmName})`}
      />
      <AlineTooltip
        arrow
        placement="top"
        title={`Some ${lower} field values differ from this document (${crmName})`}
      >
        <div className={classes.iconWrapper}>
          <StaleIcon />
        </div>
      </AlineTooltip>
    </div>
  );
}

export function IntegrationFields({
  className,
  integration,
  integrationFields,
  loading,
}: {
  integration: Apollo.Integration;
  integrationFields: DocumentCRMField[];
  loading: boolean;
  className?: string;
}) {
  /**
   * Local to this panel on purpose. A filter kept outside the component followed
   * the user into the next document and the next CRM, so the field list came up
   * pre-filtered on something they typed somewhere else.
   */
  const [fieldSearchText, setFieldSearchText] = useState('');

  return (
    <div className={className}>
      <AttributesHeader searchText={fieldSearchText} setSearchText={setFieldSearchText} />
      <IntegrationFieldsBody
        integration={integration}
        integrationFields={integrationFields}
        loading={loading}
        searchText={fieldSearchText}
      />
    </div>
  );
}

export function AttributesHeader({
  searchText,
  setSearchText,
}: {
  searchText: string;
  setSearchText: (value: string) => void;
}) {
  const { classes } = useStyles();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const fieldRecipient = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.documentUi.fieldRecipient
  );
  const setFieldRecipient = useCallback(
    (fieldRecipient: ExistingFieldRecipient | null): void => {
      if (!tabId) return;
      getEditorTabStateActionsForTabId(tabId).documentUi.setFieldRecipient(fieldRecipient);
    },
    [tabId]
  );

  return (
    <div className={classes.attributesHeader}>
      <div className={classes.headerSection}>
        <Typography variant="subtitle2">Assignee</Typography>
        <FieldRecipientAutoComplete
          className={classes.assigneeInput}
          value={fieldRecipient}
          onChange={option => {
            if (!option) return;
            const isNewRecipient = option?.__typename === 'NewFieldRecipient';
            if (isNewRecipient) return workflowStepsDialogOpenVar(true);
            setFieldRecipient(option);
          }}
        />
      </div>
      <div className={classes.headerSection}>
        <Typography variant="subtitle2">Integration Fields</Typography>
        <Input
          disableUnderline
          fullWidth
          className={classes.searchInput}
          placeholder="Search integration fields..."
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon className={classes.searchIcon} height={16} width={16} />
            </InputAdornment>
          }
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
      </div>
    </div>
  );
}

export const fieldAttributesSubheaderHeightPx = 48;
export const useStickyListStyles = makeStyles()(theme => ({
  divider: {
    margin: theme.spacing(0, 1.5),
  },
  listRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    overflow: 'auto',
    padding: 0,
    position: 'relative',
    width: '100%',
  },
  listSubHeader: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(0.5),
    height: fieldAttributesSubheaderHeightPx,
    lineHeight: fieldAttributesSubheaderHeightPx,
    padding: theme.spacing(0, 1.5),
    width: '100%',
  },
  ul: {
    '&:not(:first-of-type)': {
      paddingTop: theme.spacing(1),
    },
    borderBottom: `1px solid ${theme.appColors.rule}`,
    borderRadius: 3,
    padding: 0,
  },
}));

export function IntegrationFieldsBody({
  integration,
  integrationFields,
  loading,
  searchText,
}: {
  integration: Apollo.Integration;
  integrationFields: DocumentCRMField[];
  loading: boolean;
  searchText: string;
}) {
  const groupedFields = useMemo(
    () => getFieldGroups(integrationFields, searchText),
    [integrationFields, searchText]
  );

  if (loading) return <IntegrationFieldsLoading />;

  return <AttributeList groupedFields={groupedFields} integration={integration} />;
}

export function AttributeList({
  groupedFields,
  integration,
}: {
  groupedFields: FieldGroup[];
  integration: Apollo.Integration;
}) {
  const { classes } = useStyles();
  const { classes: stickyClasses } = useStickyListStyles();

  if (!groupedFields.length) return <AttributesEmpty />;

  return (
    <List className={stickyClasses.listRoot}>
      {groupedFields.map(group => (
        <li key={getFieldGroupKey(group)} className={stickyClasses.ul}>
          <Typography className={classes.categoryHeader} variant="subtitle2">
            {group.label}
          </Typography>
          <LinkedOpportunityFields integration={integration} integrationFields={group.fields} />
        </li>
      ))}
    </List>
  );
}

const useEmptyStyles = makeStyles()(_theme => ({
  emptyIntegrationFields: {
    alignItems: 'center',
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
  },
}));

export function AttributesEmpty(): JSX.Element {
  const { classes } = useEmptyStyles();

  return (
    <div className={classes.emptyIntegrationFields}>
      <Typography align="center" variant="subtitle1">
        No integration fields match your search.
      </Typography>
    </div>
  );
}

export function LinkedOpportunityFields({
  integration,
  integrationFields,
}: {
  integration: Apollo.Integration;
  integrationFields: DocumentCRMField[];
}) {
  const { classes } = useStyles();
  const tabId = useMaybeEditorRuntime()?.tabId ?? null;
  const fieldRecipient = useEditorTabStateSelectorForTabId(
    tabId,
    state => state.documentUi.fieldRecipient
  );
  const sortedIntegrationFields = useMemo(
    () => [...integrationFields].sort((a, b) => a.title.localeCompare(b.title)),
    [integrationFields]
  );

  return (
    <div className={classes.attributeCards}>
      {sortedIntegrationFields.map((integrationField, index) => {
        const isIntegrationFieldInDoc = Boolean(integrationField.fieldInDocInfo?.nodeId);

        return (
          <IntegrationFieldButton
            key={index}
            disabledReason={!fieldRecipient ? `Select a recipient to use ${integration} field` : ''}
            dragField={{
              __typename: 'DocumentVariable',
              actionType: isIntegrationFieldInDoc ? 'insert' : 'create',
              config: null,
              integrationLink: {
                attributePath: integrationField.path,
                integration,
              },
              isDynamic: false,
              label: integrationField.title,
              nodeId: integrationField.fieldInDocInfo?.nodeId ?? uuid4(),
              value: integrationField.value,
              variant: Apollo.MetadataVariant.Text,
            }}
            integration={integration}
            integrationField={integrationField}
          />
        );
      })}
    </div>
  );
}

const LOADING_SECTIONS = [
  {
    cardCount: 5,
    label: 'Opportunity',
    labelWidth: 96,
    titleWidths: [148, 112, 168, 96, 132],
    valueWidths: [72, 48, 120, 40, 88],
  },
  {
    cardCount: 3,
    label: 'Company',
    labelWidth: 160,
    titleWidths: [120, 100, 140],
    valueWidths: [96, 64, 80],
  },
  {
    cardCount: 2,
    label: 'Owner',
    labelWidth: 140,
    titleWidths: [88, 108],
    valueWidths: [56, 72],
  },
] as const;

/** Skeleton that mirrors the loaded list: sticky category headers + multi-line field cards. */
export function IntegrationFieldsLoading() {
  const { classes } = useStyles();
  const { classes: stickyClasses } = useStickyListStyles();

  return (
    <List aria-busy aria-label="Loading integration fields" className={stickyClasses.listRoot}>
      {LOADING_SECTIONS.map(section => (
        <li key={section.label} className={classes.loadingSection}>
          <Typography className={classes.categoryHeader} variant="subtitle2">
            <Skeleton animation="wave" height={20} variant="text" width={section.labelWidth} />
          </Typography>
          <div className={classes.attributeCards}>
            {Array.from({ length: section.cardCount }, (_unused, index) => {
              const titleWidth = section.titleWidths[index % section.titleWidths.length] ?? 120;
              const valueWidth = section.valueWidths[index % section.valueWidths.length] ?? 72;
              return (
                <IntegrationFieldLoadingCard
                  key={`${section.label}-${index}`}
                  titleWidth={titleWidth}
                  valueWidth={valueWidth}
                />
              );
            })}
          </div>
        </li>
      ))}
    </List>
  );
}

function IntegrationFieldLoadingCard({
  titleWidth,
  valueWidth,
}: {
  titleWidth: number;
  valueWidth: number;
}) {
  const { classes } = useStyles();

  return (
    <div className={classes.loadingCard}>
      <div className={classes.loadingCardBody}>
        <Skeleton animation="wave" height={18} variant="text" width={titleWidth} />
        <Skeleton animation="wave" height={16} variant="text" width={valueWidth} />
      </div>
    </div>
  );
}
